import type { RefObject } from 'react';
import { PaymentMethodEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import type { AppchargePaymentElementCard } from '@/types/appchargePaymentElement';
import { Errors } from '@/constants/errors';
import { checkIfServerFinalizeFailure } from '@/utils/error';
import { buildAppchargePaymentElementCardRequest } from '@/utils/appchargePaymentElement';

export const handleAppchargePaymentElementCardPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>
): Promise<unknown> => {
  try {
    const paymentMethodSettings = checkout.paymentMethodsSettings.find(
      (pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
    );

    if (!paymentMethodSettings) {
      throw new Error(Errors.payment.paymentFailed);
    }

    const cardRef = checkout.card.cardElementRef as RefObject<AppchargePaymentElementCard> | null;
    const card = cardRef?.current;
    if (!card) {
      throw new Error(Errors.payment.paymentFailed);
    }

    const validation = await card.validate();
    if (!validation.valid) {
      throw new Error(Errors.payment.paymentFailed);
    }

    const cardCollectResult = await card.collect();
    if (!cardCollectResult.ok) {
      throw new Error(Errors.payment.paymentFailed);
    }

    const request = buildAppchargePaymentElementCardRequest({
      cardCollectResult,
      paymentMethodSettings,
      email: checkout.customer.email ?? '',
      cardholderName: checkout.card.initialCardPayload?.cardholderName ?? '',
      shouldSaveCard: checkout.card.initialCardPayload?.isSaveCard ?? false,
    });

    return await sendPaymentToServer(request);
  } catch (error) {
    if (checkIfServerFinalizeFailure(error)) {
      throw error;
    }
    console.error(Errors.payment.paymentFailed, { cause: error });
    throw new Error(Errors.payment.paymentFailed);
  }
};
